/**
 * How the Milk sales screen says a refusal, shared by its tickets and contracts (`MilkSales.tsx`) and its sales
 * offers (`SalesOffers.tsx`).
 *
 * The screen holds parts for different roles (spec 2026-10-14-sales-offers-before-contract-design.md, OF-7):
 * the tickets and contracts need the payments role, the offers trading or management. A person holding only
 * one of them sees the other parts say so in one sentence, not as an error.
 */
import { errorCode, PlannerApiError } from "../api";
import { Failure } from "../components/Failure";

/** The API's own sentence, which names what to do. */
export function detailOf(error: unknown): string {
  if (error instanceof PlannerApiError && error.detail) return error.detail;
  return "The planner could not be reached, or refused without saying why.";
}

/** A refusal of one part of the screen, said for that part: Failure's sentence is about the plan. */
export function ScreenRefused({
  error,
  part = "tickets",
}: {
  error: unknown;
  /** tickets: the held loads (payments role); offers: the sales offers with their prices; contracts: the
   *  contracts with their prices (Milk trading); sales: the week plan and the loads sold, in litres (Milk sales). */
  part?: "tickets" | "offers" | "contracts" | "sales";
}) {
  const code = errorCode(error);
  if (code === "MILK_SALES_NOT_AVAILABLE") {
    return (
      <section className="panel">
        <p>
          {{ offers: "Sales offers", contracts: "Sales contracts", sales: "Milk sales", tickets: "Sales tickets" }[part]} cannot be shown yet. The database tables they
          need, or this application's access to them, are not there.
        </p>
        <p className="milk-sales__detail">{detailOf(error)}</p>
      </section>
    );
  }
  if (code === "ROLE_NOT_PERMITTED") {
    return (
      <p className="empty">
        {
          {
            offers: "The sales offers are for milk trading, the milk office and management: your account does not have a role that reads them.",
            contracts: "The contracts and their prices are for milk trading, the milk office and management: your account does not have a role that reads them.",
            sales: "Milk sales is for the transport office, milk trading, the milk office and management: your account does not have a role that reads it.",
            tickets: "This part is for the milk office: it needs the payments role, which your account does not have.",
          }[part]
        }
      </p>
    );
  }
  if (code === "USER_NOT_PROVISIONED") {
    return (
      <div className="failure" role="alert">
        <p className="failure__sentence">
          This screen is for the milk office, milk trading and the site director: your account has no access to
          the planner. Ask the administrator to give you access.
        </p>
        <p className="failure__detail">The planner API said: {detailOf(error)}</p>
      </div>
    );
  }
  return <Failure error={error} />;
}

/** After a refusal, focus goes to the first field to fix (WCAG 3.3.1). A select marks its root
 *  invalid, not its button, so both are looked for, in the order the form shows them. */
export function focusFirstInvalid(form: HTMLElement | null) {
  window.setTimeout(() => {
    form
      ?.querySelector<HTMLElement>('input[aria-invalid="true"], textarea[aria-invalid="true"], [data-invalid] > button')
      ?.focus();
  }, 0);
}
